import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { api, setAuthToken, clearAuthToken } from '../lib/api';
import type {
  User,
  LoginCredentials,
  LoginResponse,
  Permission,
  AuthState,
} from '../types/auth';

const TOKEN_KEY = 'nano-stock-token';
const REFRESH_TOKEN_KEY = 'nano-stock-refresh-token';
const USER_KEY = 'nano-stock-user';

interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const [state, setState] = useState<AuthState>({
    user: null,
    accessToken: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Initialize auth state from storage with token validation
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_KEY);

        if (storedToken && storedUser) {
          // Set token for API calls
          setAuthToken(storedToken);

          try {
            // Validate token with backend and get fresh user data
            const response = await api.get<User>('/auth/me');
            const freshUser = response.data;

            // Update stored user with fresh data
            localStorage.setItem(USER_KEY, JSON.stringify(freshUser));

            setState({
              user: freshUser,
              accessToken: storedToken,
              isAuthenticated: true,
              isLoading: false,
            });
          } catch (validationError) {
            // Token invalid or expired, try refresh
            const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
            if (refreshToken) {
              try {
                const refreshResponse = await api.post<{
                  accessToken: string;
                  refreshToken?: string;
                  expiresIn: number;
                }>('/auth/refresh', { refreshToken });

                const { accessToken: newToken, refreshToken: newRefreshToken } = refreshResponse.data;

                localStorage.setItem(TOKEN_KEY, newToken);
                if (newRefreshToken) {
                  localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
                }
                setAuthToken(newToken);

                // Get fresh user data with new token
                const userResponse = await api.get<User>('/auth/me');
                localStorage.setItem(USER_KEY, JSON.stringify(userResponse.data));

                setState({
                  user: userResponse.data,
                  accessToken: newToken,
                  isAuthenticated: true,
                  isLoading: false,
                });
                return;
              } catch {
                // Refresh also failed, clear everything
              }
            }

            // Clear invalid tokens
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            clearAuthToken();
            setState((prev) => ({ ...prev, isLoading: false }));
          }
        } else {
          setState((prev) => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        clearAuthToken();
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    initializeAuth();
  }, []);

  // Listen for session expired events from API interceptor
  useEffect(() => {
    const handleSessionExpired = () => {
      // Clear storage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      clearAuthToken();

      setState({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
      });

      navigate('/login', { replace: true });
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [navigate]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await api.post<LoginResponse>('/auth/login', credentials);
      const { user, accessToken, refreshToken } = response.data;

      // Store tokens and user
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(user));

      setAuthToken(accessToken);

      setState({
        user,
        accessToken,
        isAuthenticated: true,
        isLoading: false,
      });

      // Navigate to dashboard after successful login
      navigate('/', { replace: true });
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [navigate]);

  const logout = useCallback(() => {
    // Clear storage
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);

    clearAuthToken();

    setState({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
    });

    navigate('/login', { replace: true });
  }, [navigate]);

  const refreshAuth = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (!refreshToken) {
      logout();
      return;
    }

    try {
      const response = await api.post<{
        accessToken: string;
        refreshToken?: string;
        expiresIn: number;
      }>('/auth/refresh', { refreshToken });

      const { accessToken, refreshToken: newRefreshToken } = response.data;

      // Store new tokens
      localStorage.setItem(TOKEN_KEY, accessToken);
      if (newRefreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
      }
      setAuthToken(accessToken);

      setState((prev) => ({
        ...prev,
        accessToken,
      }));
    } catch (error) {
      console.error('Failed to refresh token:', error);
      logout();
    }
  }, [logout]);

  const hasPermission = useCallback(
    (permission: Permission | string): boolean => {
      if (!state.user) return false;

      // Admins and superadmins have all permissions
      if (state.user.role === 'admin' || state.user.role === 'superadmin') return true;

      // Check if user has the specific permission
      // Cast to string[] for includes comparison since Permission extends string
      const userPermissions = (state.user.permissions || []) as string[];
      if (userPermissions.includes(permission as string)) return true;

      // Check for wildcard permissions (e.g., 'items:*' matches 'items:read')
      const [resource] = permission.split(':');
      return (
        userPermissions.includes(`${resource}:*`) ||
        userPermissions.includes('*:*')
      );
    },
    [state.user]
  );

  const hasAnyPermission = useCallback(
    (permissions: Permission[]): boolean => {
      return permissions.some((permission) => hasPermission(permission));
    },
    [hasPermission]
  );

  const hasAllPermissions = useCallback(
    (permissions: Permission[]): boolean => {
      return permissions.every((permission) => hasPermission(permission));
    },
    [hasPermission]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      logout,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      refreshAuth,
    }),
    [state, login, logout, hasPermission, hasAnyPermission, hasAllPermissions, refreshAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

// Hook for checking permissions in components
export function usePermission(permission: Permission): boolean {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
}
